/** EmitIQ's draft of why the total moved, for an Approver to edit and save - under the bridge.
 *
 * **The draft is built on the server from the bridge's own numbers, with no model** (`reporting/
 * variance_narrative.py`): fixed sentences, each figure rounded once there, and each figure carrying
 * the window and the sites that open it (rule 3). This screen shows the sentences in an editor and
 * lists the figures beside it as the same register links the bridge uses, so the person editing can
 * open every number they are about to put their name to.
 *
 * What it will not do, said once: it does not save the draft by itself (a person presses Save),
 * it does not say a filing must explain a movement (nothing in the verification log says so), and it
 * does not hide the word *draft*. What is saved is the company's own statement, versioned and
 * ledgered as an identifier and a digest and never as words (ADR-0033).
 *
 * **A control, or the sentence saying who may use it - never neither** (design rule 6,
 * `formOrWhoCan`): an Admin or an Approver is offered the draft; every other role is shown what was
 * saved, if anything, and told who writes it.
 */

import { Link } from "@tanstack/react-router";
import { useState } from "react";

import { Refused } from "../api/refusal";
import type { Scope2Method } from "../api/queries";
import { useSaveVarianceNarrative, useVarianceNarrative } from "../api/varianceNarrative";
import type { VarianceFigure, VarianceSaved } from "../api/varianceNarrative";
import { ReadFailed, WriteFailed } from "../components/Loaded";
import { InfoPopover } from "../components/ui";
import { customerText } from "../lib/customerWords";
import { shortDate } from "../lib/format";
import own from "./VarianceNarrative.module.css";

/** The register a figure opens: the same links the bridge's own numbers use (`ReportsBridge.tsx`). */
function FigureLink({
  figure,
  method,
  obligationId,
  entityName,
}: {
  figure: VarianceFigure;
  method: Scope2Method;
  obligationId: string;
  entityName: string;
}) {
  const window = `${shortDate(figure.start)} to ${shortDate(figure.end)}`;
  const label = `${figure.label}: ${figure.text} tonnes. Open the figures for ${window}`;
  if (figure.kind === "total") {
    return (
      <Link
        to="/emissions"
        search={{ method, from: figure.start, to: figure.end, entity: entityName, tab: "register" }}
        aria-label={label}
      >
        {figure.text}
      </Link>
    );
  }
  const [only] = figure.site_ids;
  const one = figure.site_ids.length === 1 && only !== undefined;
  return (
    <Link
      to="/emissions"
      search={
        one
          ? { method, from: figure.start, to: figure.end, site: only, tab: "register" }
          : {
              method,
              from: figure.start,
              to: figure.end,
              sites: figure.site_ids.join(","),
              filing: obligationId,
              tab: "register",
            }
      }
      aria-label={label}
    >
      {figure.text}
    </Link>
  );
}

export function VarianceNarrative({
  obligationId,
  method,
  entityName,
}: {
  obligationId: string;
  method: Scope2Method;
  entityName: string;
}) {
  const read = useVarianceNarrative(obligationId, method);
  const save = useSaveVarianceNarrative(obligationId);
  // `null` is "not editing"; a string is the editor's text.
  const [text, setText] = useState<string | null>(null);
  // Whether the editor was opened on EmitIQ's draft, so the figures it was built from are listed.
  const [fromDraft, setFromDraft] = useState(false);
  // The edition the editor was opened against; moved only by this editor's own save or a re-apply.
  const [opened, setOpened] = useState(0);
  const [fresh, setFresh] = useState<VarianceSaved | null | undefined>(undefined);
  const [reapplying, setReapplying] = useState(false);

  const data = read.data;
  const saved = data?.saved ?? null;
  const draft = data?.draft ?? null;

  const open = (initial: string, withDraft: boolean) => {
    setText(initial);
    setFromDraft(withDraft);
    setOpened(saved?.version ?? 0);
    setFresh(undefined);
    save.reset();
  };
  const close = () => {
    setText(null);
    setFromDraft(false);
    setFresh(undefined);
    save.reset();
  };

  const send = (version: number) => {
    if (text === null) return Promise.resolve();
    setFresh(undefined);
    return save.mutateAsync({ text, expectedVersion: version }).then(
      () => close(),
      (error: unknown) => {
        // Said beside the editor with what is saved now; what was typed is kept.
        if (error instanceof Refused && error.isStale) {
          // A refetch that failed leaves `fresh` unset: the sentence then says nothing about what is
          // saved now, rather than claiming nothing is.
          void read.refetch().then((again) => {
            if (again.data) setFresh(again.data.saved);
          });
        }
      },
    );
  };

  const reapply = async () => {
    setReapplying(true);
    try {
      const again = await read.refetch();
      // Not knowing the edition is not edition 0: a failed read sends nothing.
      if (!again.data) return;
      const version = again.data.saved?.version ?? 0;
      setOpened(version);
      await send(version);
    } finally {
      setReapplying(false);
    }
  };

  if (read.error) {
    return (
      <div className={own.root} data-testid="variance-failed">
        <ReadFailed
          of="The explanation of the change"
          error={read.error}
          onRetry={() => void read.refetch()}
        />
      </div>
    );
  }
  if (!data) return null;

  return (
    <section className={own.root} data-testid="variance-narrative" aria-label="Explaining the change">
      <header className={own.head}>
        <h3 className={own.title}>Explaining the change</h3>
        <span className={own.tag}>EmitIQ&rsquo;s draft</span>
        <InfoPopover infoKey="filings.variance-narrative" />
      </header>

      {text === null ? (
        <>
          {saved ? (
            <div className={own.saved} data-testid="variance-saved">
              <p className={own.words}>{saved.text}</p>
              <span className={own.meta}>
                {saved.saved_by ? `Saved by ${saved.saved_by}` : "Saved"}
                {saved.saved_at ? `, ${shortDate(saved.saved_at.slice(0, 10))}` : ""}
              </span>
            </div>
          ) : null}
          {data.may_draft ? (
            <div className={own.row}>
              {draft?.available ? (
                <button
                  type="button"
                  className={own.button}
                  data-testid="variance-draft"
                  onClick={() => open(draft.text, true)}
                >
                  {saved ? "Start again from the bridge" : "Draft from the bridge"}
                </button>
              ) : null}
              {saved ? (
                <button
                  type="button"
                  className={own.button}
                  data-testid="variance-edit"
                  onClick={() => open(saved.text, false)}
                >
                  Edit
                </button>
              ) : null}
              {draft && !draft.available ? (
                <span className={own.note} data-testid="variance-unavailable">
                  {customerText(draft.unavailable_message)}
                </span>
              ) : null}
            </div>
          ) : (
            <span className={own.note} data-testid="variance-who-can">
              An Admin or an Approver writes this explanation.
            </span>
          )}
        </>
      ) : (
        <div className={own.editor} data-testid="variance-editor">
          <textarea
            rows={9}
            aria-label={fromDraft ? "EmitIQ's draft, for you to edit" : "Your explanation of the change"}
            value={text}
            onChange={(event) => setText(event.target.value)}
          />
          {fromDraft && draft ? (
            <ul className={own.figures} data-testid="variance-figures" aria-label="Where each figure comes from">
              {uniqueFigures(draft.sentences.flatMap((sentence) => sentence.figures)).map((figure) => (
                <li key={figureKey(figure)} className={own.figure}>
                  <span className={own.figureNumber + " num"}>
                    <FigureLink
                      figure={figure}
                      method={method}
                      obligationId={obligationId}
                      entityName={entityName}
                    />
                  </span>
                  <span className={own.figureLabel}>
                    {customerText(figure.label)}
                    {figure.derived ? ", a difference of the figures behind it" : ""}
                  </span>
                </li>
              ))}
            </ul>
          ) : null}
          <div className={own.row}>
            <button
              type="button"
              className={own.save}
              data-testid="variance-save"
              disabled={save.isPending || text.trim().length === 0}
              onClick={() => void send(opened)}
            >
              {save.isPending ? "Saving…" : "Save"}
            </button>
            <button type="button" className={own.button} data-testid="variance-cancel" onClick={close}>
              Cancel
            </button>
            {save.isError ? (
              <WriteFailed
                error={save.error}
                of="this explanation"
                className={own.error}
                onRetry={() => void send(opened)}
                nowReads={
                  fresh === undefined ? undefined : fresh === null ? "nothing saved" : `“${shorten(fresh.text)}”`
                }
                onReapply={() => void reapply()}
                reapplying={reapplying}
              />
            ) : null}
          </div>
        </div>
      )}
    </section>
  );
}

function figureKey(figure: VarianceFigure): string {
  return [figure.kind, figure.window, figure.site_ids.join(","), figure.text].join("|");
}

/** One link per distinct figure: a number repeated in two sentences opens the same register. */
function uniqueFigures(figures: readonly VarianceFigure[]): VarianceFigure[] {
  const seen = new Set<string>();
  return figures.filter((figure) => {
    const key = figureKey(figure);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function shorten(text: string): string {
  return text.length > 160 ? `${text.slice(0, 160)}…` : text;
}
